import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ConditionManager from "./ConditionManager";
import StudyManager from "./admin/StudyManager";
import TemplatePoolManager from "./admin/TemplatePoolManager";
import {
  FaUsers,
  FaComments,
  FaShare,
  FaHeart,
  FaHashtag,
  FaChartBar,
  FaDownload,
  FaTrash,
  FaPlus,
  FaRobot,
  FaUser,
  FaClock,
  FaCog,
  FaFlask,
  FaEye,
  FaLayerGroup,
  FaCheckCircle,
  FaExclamationCircle,
  FaArrowRight,
  FaNewspaper,
  FaDatabase,
} from "react-icons/fa";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [stats, setStats] = useState({});
  const [globalAnalytics, setGlobalAnalytics] = useState(null);
  const [error, setError] = useState("");
  const [selectedCondition, setSelectedCondition] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("studies"); // "studies" | "conditions" | "templates" | "overview" | "analytics"

  // Read admin token directly from localStorage
  const getAdminToken = () => localStorage.getItem("admin_token");

  // Fetch dashboard data
  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalyticsData();
    } else if (activeTab === "overview") {
      fetchGlobalAnalytics();
    }
  }, [selectedCondition, activeTab]);

  const fetchGlobalAnalytics = async () => {
    setLoading(true);
    const token = getAdminToken();
    try {
      const { data: analytics } = await api.get(`/admin/analytics/global`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGlobalAnalytics(analytics);
    } catch (error) {
      console.error("Error fetching global analytics:", error);
      setError("Failed to fetch global analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    const token = getAdminToken();
    try {
      const { data: fetchedData } = await api.get(`/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { condition: selectedCondition },
      });
      setData(fetchedData);
      calculateStats(fetchedData);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (rawData) => {
    const users = rawData.users || [];
    const messages = rawData.messages || [];
    const comments = rawData.comments || [];
    const interactions = rawData.interactions || [];

    // Basic counts
    const totalUsers = users.length;
    const humanUsers = users.filter(u => !u.artificial).length;
    const aiUsers = users.filter(u => u.artificial).length;
    const totalMessages = messages.length;
    const totalComments = comments.length;
    const totalInteractions = interactions.length;

    // Activity stats
    const totalLikes = interactions.filter(i => i.type === 'like').length;
    const totalReposts = interactions.filter(i => i.type === 'repost').length;
    const totalFollows = interactions.filter(i => i.type === 'follow').length;

    // Recent activity (last 24 hours)
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recentMessages = messages.filter(m => new Date(m.timestamp) > oneDayAgo).length;
    const recentInteractions = interactions.filter(i => new Date(i.timestamp) > oneDayAgo).length;

    // Engagement rate
    const engagementRate = totalMessages > 0 ? (totalInteractions / totalMessages * 100).toFixed(1) : 0;

    // Most active users (top 5)
    const userActivityMap = {};
    interactions.forEach(interaction => {
      userActivityMap[interaction.userId] = (userActivityMap[interaction.userId] || 0) + 1;
    });
    const topUsers = Object.entries(userActivityMap)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([userId, count]) => {
        const user = users.find(u => u._id === userId);
        return { user: user?.username || userId, count, artificial: user?.artificial };
      });

    // Average time on platform (for humans only)
    const humanTimeData = users.filter(u => !u.artificial && u.timeSpentOnPlatform);
    const avgTimeMinutes = humanTimeData.length > 0
      ? (humanTimeData.reduce((sum, u) => sum + u.timeSpentOnPlatform, 0) / humanTimeData.length / (1000 * 60)).toFixed(1)
      : 0;

    // Hashtag analysis
    const hashtags = {};
    messages.forEach(message => {
      if (message.hashtags) {
        message.hashtags.forEach(tag => {
          hashtags[tag] = (hashtags[tag] || 0) + 1;
        });
      }
    });
    const topHashtags = Object.entries(hashtags)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10);

    setStats({
      totalUsers,
      humanUsers,
      aiUsers,
      totalMessages,
      totalComments,
      totalInteractions,
      totalLikes,
      totalReposts,
      totalFollows,
      recentMessages,
      recentInteractions,
      engagementRate,
      topUsers,
      avgTimeMinutes,
      topHashtags
    });
  };

  const exportData = async () => {
    const token = getAdminToken();
    try {
      const response = await api.get(`/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { condition: selectedCondition, export: true },
      });

      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `spiders-data-${selectedCondition || 'all'}-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to export data:", e);
      setError("Failed to export data");
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => (
    <div className={`bg-gray-800 p-6 rounded-lg border-l-4 border-${color}-400`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-300 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
        </div>
        <Icon className={`text-${color}-400 text-2xl`} />
      </div>
    </div>
  );

  const TabButton = ({ id, label, icon: Icon, isActive }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center px-4 py-2 rounded-t-lg transition-colors ${
        isActive
          ? "bg-gray-800 text-white border-b-2 border-blue-500"
          : "bg-gray-700 text-gray-400 hover:text-white"
      }`}
    >
      <Icon className="mr-2" />
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2">SPIDERS Research Dashboard</h1>
          <p className="text-gray-300">Monitor experiments, manage conditions, and analyze participant behavior</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-0">
          <TabButton id="studies" label="Studies" icon={FaLayerGroup} isActive={activeTab === "studies"} />
          <TabButton id="conditions" label="Conditions" icon={FaFlask} isActive={activeTab === "conditions"} />
          <TabButton id="templates" label="Templates" icon={FaDatabase} isActive={activeTab === "templates"} />
          <TabButton id="overview" label="Overview" icon={FaEye} isActive={activeTab === "overview"} />
          <TabButton id="analytics" label="Analytics" icon={FaChartBar} isActive={activeTab === "analytics"} />
        </div>

        {/* Tab Content */}
        <div className="bg-gray-800 rounded-b-lg rounded-tr-lg p-6">
          {activeTab === "conditions" && <ConditionManager />}

          {activeTab === "studies" && <StudyManager />}
          {activeTab === "templates" && <TemplatePoolManager />}

          {activeTab === "analytics" && (
            <>
              {/* Condition Filter */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="text-gray-300">Filter by Condition:</label>
                  <select
                    value={selectedCondition}
                    onChange={(e) => setSelectedCondition(e.target.value)}
                    className="p-2 bg-gray-700 text-white rounded border border-gray-600"
                  >
                    <option value="">All Conditions</option>
                    {data.conditions?.map((cond) => (
                      <option key={cond} value={cond}>
                        {cond}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={exportData}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
                >
                  <FaDownload className="mr-2" /> Export Data
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                </div>
              ) : (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard
                      title="Total Users"
                      value={stats.totalUsers || 0}
                      subtitle={`${stats.humanUsers} human • ${stats.aiUsers} AI`}
                      icon={FaUsers}
                      color="blue"
                    />
                    <StatCard
                      title="Total Messages"
                      value={stats.totalMessages || 0}
                      subtitle={`${stats.recentMessages} in last 24h`}
                      icon={FaComments}
                      color="green"
                    />
                    <StatCard
                      title="Total Interactions"
                      value={stats.totalInteractions || 0}
                      subtitle={`${stats.engagementRate}% engagement rate`}
                      icon={FaShare}
                      color="purple"
                    />
                    <StatCard
                      title="Avg. Session Time"
                      value={`${stats.avgTimeMinutes || 0}m`}
                      subtitle="Human users only"
                      icon={FaClock}
                      color="orange"
                    />
                  </div>

                  {/* Detailed Metrics */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Activity Breakdown */}
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4">Activity Breakdown</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-2">
                            <FaHeart className="text-red-400" />
                            <span className="text-gray-300">Likes</span>
                          </div>
                          <span className="text-white font-semibold">{stats.totalLikes || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-2">
                            <FaShare className="text-blue-400" />
                            <span className="text-gray-300">Reposts</span>
                          </div>
                          <span className="text-white font-semibold">{stats.totalReposts || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-2">
                            <FaComments className="text-green-400" />
                            <span className="text-gray-300">Comments</span>
                          </div>
                          <span className="text-white font-semibold">{stats.totalComments || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-2">
                            <FaUsers className="text-purple-400" />
                            <span className="text-gray-300">Follows</span>
                          </div>
                          <span className="text-white font-semibold">{stats.totalFollows || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Top Active Users */}
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4">Most Active Users</h3>
                      <div className="space-y-3">
                        {stats.topUsers?.slice(0, 5).map((userStat, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                              {userStat.artificial ? (
                                <FaRobot className="text-blue-400" />
                              ) : (
                                <FaUser className="text-green-400" />
                              )}
                              <span className="text-gray-300">{userStat.user}</span>
                              {userStat.artificial && (
                                <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">AI</span>
                              )}
                            </div>
                            <span className="text-white font-semibold">{userStat.count} actions</span>
                          </div>
                        ))}
                        {(!stats.topUsers || stats.topUsers.length === 0) && (
                          <p className="text-gray-400">No activity data yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trending Hashtags */}
                  {stats.topHashtags && stats.topHashtags.length > 0 && (
                    <div className="mt-6 bg-gray-700 rounded-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                        <FaHashtag className="mr-2" />
                        Trending Hashtags
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {stats.topHashtags.slice(0, 10).map(([hashtag, count]) => (
                          <div key={hashtag} className="bg-gray-600 rounded-lg p-3 text-center">
                            <div className="text-blue-400 font-semibold">#{hashtag}</div>
                            <div className="text-gray-300 text-sm">{count} posts</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === "overview" && (
            <>
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                </div>
              ) : globalAnalytics ? (
                <>
                  {/* Global Summary Cards */}
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <FaChartBar className="mr-2 text-blue-400" />
                      Global Research Summary
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      <StatCard
                        title="Conditions"
                        value={globalAnalytics.global?.conditions || 0}
                        subtitle={`${globalAnalytics.global?.activeConditions || 0} active`}
                        icon={FaFlask}
                        color="purple"
                      />
                      <StatCard
                        title="Instances"
                        value={globalAnalytics.global?.realInstances || 0}
                        subtitle={`${globalAnalytics.global?.completedInstances || 0} completed`}
                        icon={FaLayerGroup}
                        color="blue"
                      />
                      <StatCard
                        title="Human Participants"
                        value={globalAnalytics.global?.totalHumanParticipants || 0}
                        subtitle="across all conditions"
                        icon={FaUser}
                        color="green"
                      />
                      <StatCard
                        title="AI Agents"
                        value={globalAnalytics.global?.totalAIAgents || 0}
                        subtitle="across all conditions"
                        icon={FaRobot}
                        color="cyan"
                      />
                      <StatCard
                        title="Human Messages"
                        value={globalAnalytics.global?.totalHumanMessages || 0}
                        subtitle={`${globalAnalytics.global?.totalHumanComments || 0} comments`}
                        icon={FaComments}
                        color="yellow"
                      />
                      <StatCard
                        title="Human Interactions"
                        value={globalAnalytics.global?.totalHumanInteractions || 0}
                        subtitle="likes, follows, etc."
                        icon={FaHeart}
                        color="red"
                      />
                    </div>
                  </div>

                  {/* Cross-Condition Comparison Table */}
                  <div className="mb-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <FaFlask className="mr-2 text-purple-400" />
                      Condition Comparison (Human vs AI)
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-gray-700 text-gray-300 text-sm">
                            <th className="p-3 rounded-tl-lg">Condition</th>
                            <th className="p-3">Status</th>
                            <th className="p-3 text-center">Instances</th>
                            <th className="p-3 text-center">
                              <span className="text-green-400">Human</span> / <span className="text-blue-400">AI</span> Users
                            </th>
                            <th className="p-3 text-center">
                              <span className="text-green-400">Human</span> / <span className="text-blue-400">AI</span> Messages
                            </th>
                            <th className="p-3 text-center">
                              <span className="text-green-400">Human</span> / <span className="text-blue-400">AI</span> Comments
                            </th>
                            <th className="p-3 text-center">Avg/Human</th>
                            <th className="p-3 rounded-tr-lg">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {globalAnalytics.conditions?.map((cond, idx) => (
                            <tr
                              key={cond.condition.id}
                              className={`${idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'} hover:bg-gray-700 transition-colors`}
                            >
                              <td className="p-3">
                                <div className="font-semibold text-white">{cond.condition.name}</div>
                                <div className="text-gray-400 text-xs">{cond.condition.topic || 'No topic'}</div>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  cond.condition.status === 'active' ? 'bg-green-900 text-green-200' :
                                  cond.condition.status === 'ready' ? 'bg-blue-900 text-blue-200' :
                                  cond.condition.status === 'archived' ? 'bg-red-900 text-red-200' :
                                  'bg-gray-600 text-gray-300'
                                }`}>
                                  {cond.condition.status}
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                <span className="text-white">{cond.instances.real}</span>
                                {cond.instances.test > 0 && (
                                  <span className="text-gray-400 text-xs ml-1">(+{cond.instances.test} test)</span>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <span className="text-green-400 font-semibold">{cond.users.human}</span>
                                <span className="text-gray-500 mx-1">/</span>
                                <span className="text-blue-400">{cond.users.ai}</span>
                              </td>
                              <td className="p-3 text-center">
                                <span className="text-green-400 font-semibold">{cond.content.messages.byHuman}</span>
                                <span className="text-gray-500 mx-1">/</span>
                                <span className="text-blue-400">{cond.content.messages.byAI}</span>
                              </td>
                              <td className="p-3 text-center">
                                <span className="text-green-400 font-semibold">{cond.content.comments.byHuman}</span>
                                <span className="text-gray-500 mx-1">/</span>
                                <span className="text-blue-400">{cond.content.comments.byAI}</span>
                              </td>
                              <td className="p-3 text-center">
                                <div className="text-xs">
                                  <div className="text-yellow-400">{cond.engagement.avgMessagesPerHuman} msg</div>
                                  <div className="text-purple-400">{cond.engagement.avgCommentsPerHuman} cmt</div>
                                  <div className="text-red-400">{cond.engagement.avgLikesPerHuman} like</div>
                                </div>
                              </td>
                              <td className="p-3">
                                <button
                                  onClick={() => navigate(`/admin/conditions/${cond.condition.id}/analytics`)}
                                  className="text-blue-400 hover:text-blue-300 flex items-center text-sm"
                                >
                                  Details <FaArrowRight className="ml-1" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Engagement Comparison Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Human Engagement Summary */}
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                        <FaUser className="mr-2 text-green-400" />
                        Human Participant Engagement
                      </h3>
                      <div className="space-y-4">
                        {globalAnalytics.conditions?.filter(c => c.users.human > 0).map(cond => (
                          <div key={cond.condition.id} className="bg-gray-800 rounded p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-white font-medium">{cond.condition.name}</span>
                              <span className="text-gray-400 text-sm">{cond.users.human} participants</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-yellow-400 font-semibold">{cond.engagement.avgMessagesPerHuman}</div>
                                <div className="text-gray-400">Msg/user</div>
                              </div>
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-purple-400 font-semibold">{cond.engagement.avgCommentsPerHuman}</div>
                                <div className="text-gray-400">Cmt/user</div>
                              </div>
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-red-400 font-semibold">{cond.engagement.avgLikesPerHuman}</div>
                                <div className="text-gray-400">Like/user</div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {globalAnalytics.conditions?.filter(c => c.users.human > 0).length === 0 && (
                          <p className="text-gray-400 text-center py-4">No human participants yet</p>
                        )}
                      </div>
                    </div>

                    {/* Feed Exposure Summary */}
                    <div className="bg-gray-700 rounded-lg p-6">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                        <FaNewspaper className="mr-2 text-cyan-400" />
                        Feed Exposure (What Humans Saw)
                      </h3>
                      <div className="space-y-4">
                        {globalAnalytics.conditions?.filter(c => c.feedExposure).map(cond => (
                          <div key={cond.condition.id} className="bg-gray-800 rounded p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-white font-medium">{cond.condition.name}</span>
                              <span className="text-gray-400 text-sm">{cond.feedExposure.totalImpressions} views</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-center text-xs">
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-blue-400 font-semibold">
                                  {((cond.feedExposure.avgProStance / (cond.feedExposure.avgProStance + cond.feedExposure.avgContraStance || 1)) * 100).toFixed(0)}%
                                </div>
                                <div className="text-gray-400">Pro stance</div>
                              </div>
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-orange-400 font-semibold">
                                  {((cond.feedExposure.avgContraStance / (cond.feedExposure.avgProStance + cond.feedExposure.avgContraStance || 1)) * 100).toFixed(0)}%
                                </div>
                                <div className="text-gray-400">Contra stance</div>
                              </div>
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-cyan-400 font-semibold">
                                  {((cond.feedExposure.avgArtificialContent / (cond.feedExposure.avgArtificialContent + cond.feedExposure.avgHumanContent || 1)) * 100).toFixed(0)}%
                                </div>
                                <div className="text-gray-400">AI content</div>
                              </div>
                              <div className="bg-gray-700 rounded p-2">
                                <div className="text-green-400 font-semibold">
                                  {((cond.feedExposure.avgHumanContent / (cond.feedExposure.avgArtificialContent + cond.feedExposure.avgHumanContent || 1)) * 100).toFixed(0)}%
                                </div>
                                <div className="text-gray-400">Human content</div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {globalAnalytics.conditions?.filter(c => c.feedExposure).length === 0 && (
                          <p className="text-gray-400 text-center py-4">No feed exposure data yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Interaction Analysis */}
                  <div className="bg-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                      <FaHeart className="mr-2 text-red-400" />
                      Interaction Analysis (Human ↔ AI)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {globalAnalytics.conditions?.filter(c => c.interactions.total > 0).map(cond => (
                        <div key={cond.condition.id} className="bg-gray-800 rounded p-4">
                          <div className="font-semibold text-white mb-3">{cond.condition.name}</div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Likes by humans:</span>
                              <span className="text-green-400">{cond.interactions.likes.givenByHumans}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Likes by AI:</span>
                              <span className="text-blue-400">{cond.interactions.likes.givenByAI}</span>
                            </div>
                            <div className="border-t border-gray-600 my-2"></div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Likes on human content:</span>
                              <span className="text-green-400">{cond.interactions.likes.onHumanContent}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Likes on AI content:</span>
                              <span className="text-blue-400">{cond.interactions.likes.onAIContent}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-gray-500 text-sm mt-4 text-center">
                    Last updated: {new Date(globalAnalytics.generatedAt).toLocaleString()}
                  </p>
                </>
              ) : (
                <div className="text-center py-12">
                  <FaChartBar className="text-6xl text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">No Data Available</h3>
                  <p className="text-gray-400">Create conditions and run instances to see analytics</p>
                </div>
              )}
            </>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-red-600 text-white p-4 rounded">
            {error}
            <button onClick={() => setError("")} className="ml-4 underline">
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
